import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseResultsAnnouncementArgs {
  /** True on `/search`-like routes where the result-count announcement is meaningful. */
  readonly isResults: boolean;
  readonly isLoading: boolean;
  /** Displayed listings count — drives the "N found" vs "none found" copy. */
  readonly count: number;
}

/**
 * Drives the aria-live SrOnly announcement on landing pages: when the results view finishes
 * loading, produce a localized "N experiences found" (or "no experiences found") string; blank
 * it in every other state so screen readers don't announce stale counts during navigation or
 * subsequent loads.
 * Shared by every landing-page variant (`All`/`Product` × `original`/`ScheduleView`) — all four
 * previously carried the same state + effect inline.
 */
const useResultsAnnouncement = ({
  count,
  isLoading,
  isResults,
}: UseResultsAnnouncementArgs): string => {
  const { t: translate } = useTranslation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (!isResults || isLoading) {
      setAnnouncement('');
      return;
    }
    setAnnouncement(
      count === 0
        ? translate('noExperiencesFound')
        : translate('experiencesFoundCount', { count }),
    );
  }, [isResults, isLoading, count, translate]);

  return announcement;
};

export default useResultsAnnouncement;
